import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';
import { REOPEN_CONSENT_EVENT } from './CookieConsent';

const LinkedInIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const SynapsisFooter = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { name: t('nav.solutions'), href: '#solutions' },
    { name: t('nav.process'), href: '#process' },
    { name: t('nav.pricing'), href: '#pricing' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  return (
    <footer className="text-white py-12 md:py-16 px-4 relative overflow-hidden" role="contentinfo">
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 grid-pattern opacity-10" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
          {/* Company */}
          <div>
            <h3 className="flex items-center gap-2 text-2xl font-bold mb-4 text-ink">
              <span className="w-2 h-2 rounded-full bg-accent-400" aria-hidden="true" />
              Synapsis
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {t('footer.companyDescription')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-gray-300 hover:text-white transition-colors flex items-center text-sm group"
                  >
                    <ArrowRightIcon className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('footer.contact')}</h4>
            <ul className="space-y-3">
              <li className="flex items-center text-gray-300 text-sm">
                <EnvelopeIcon className="w-4 h-4 mr-3 shrink-0" />
                <a href="mailto:synapsisteam.admin@gmail.com" className="hover:text-white transition-colors">
                  synapsisteam.admin@gmail.com
                </a>
              </li>
              <li className="flex items-center text-gray-300 text-sm">
                <LinkedInIcon className="w-4 h-4 mr-3 shrink-0" />
                <a
                  href="https://www.linkedin.com/company/synapsis-team2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-gray-800 pt-6 md:pt-8">
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms-conditions" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">
              {t('footer.terms')}
            </Link>
            <button
              onClick={() => window.dispatchEvent(new Event(REOPEN_CONSENT_EVENT))}
              className="text-gray-500 hover:text-gray-300 text-xs transition-colors"
            >
              {t('cookie.manage')}
            </button>
            <Link to="/cookie-policy" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">
              {t('footer.cookies')}
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800/50 mt-6 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-gray-400 text-sm">
              {t('footer.rights')}
            </p>
            <p className="text-gray-400 text-sm">
              Sitio web por{' '}
              <a
                href="https://www.lucasriera.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-400 hover:text-accent-300 transition-colors"
              >
                Lucas Riera
              </a>
              {' '}· Desarrollo & Diseño Web
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SynapsisFooter;
